import React, { useEffect, useState } from 'react';
import { Typography, Spin } from 'antd';
import { HotTable } from '@handsontable/react';
import Handsontable from 'handsontable';
import api, { monthlyMetricService } from '../../../services/api';
import dayjs from 'dayjs';
import { formatMetricValue, getCurrencySymbol, formatPercentageValue } from '../utils/formatters';

const { Text } = Typography;

// Özel formatlama fonksiyonu
const formatValue = (value, dataType, metricId, currency = 'TRY') => {
  // Tüm geçersiz değerleri kontrol et
  if (value === null || value === undefined || value === Infinity || value === -Infinity) return '-';
  
  // String olarak gelen geçersiz değerleri kontrol et
  if (typeof value === 'string') {
    const lowerValue = value.toLowerCase();
    if (lowerValue === 'nan' || lowerValue === 'infinity' || lowerValue === '-infinity' || lowerValue.includes('[object promise]')) {
      return '-';
    }
  }
  
  // Promise objesi kontrolü
  if (typeof value === 'object' && value !== null && typeof value.then === 'function') {
    console.warn('Promise değeri tespit edildi, "-" gösteriliyor:', value);
    return '-';
  }
  
  // NaN kontrolü
  if (typeof value === 'number' && isNaN(value)) {
    return '-';
  }

  // 10094 ve 10102 metrikleri için özel format (yüzde olarak göster - 100 ile çarp)
  if (metricId === 10094 || metricId === 10102) {
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && isFinite(numValue)) {
      // Değeri 100 ile çarpıp % ekle
      const percentageValue = numValue * 100;
      return `${percentageValue.toFixed(2)}%`;
    }
    return '-';
  }

  // Değerin string olarak gelme durumunda ondalık ayırıcıyı normalize et
  let normalizedValue = value;
  if (typeof value === 'string') {
    // Virgül içeren ondalık sayıları nokta ile değiştir (3,63 -> 3.63)
    if (value.includes(',') && !value.includes('.')) {
      normalizedValue = value.replace(',', '.');
    }
  }

  switch (dataType) {
    case 'currency':
      // Para birimi için kuruşları kaldır
      const numValue = parseFloat(normalizedValue);
      if (!isNaN(numValue) && isFinite(numValue)) {
        return Math.round(numValue).toLocaleString('tr-TR') + ' ' + getCurrencySymbol(currency);
      }
      return '-';
    case 'percentage':
      const percentValue = parseFloat(normalizedValue);
      if (!isNaN(percentValue) && isFinite(percentValue)) {
        return formatPercentageValue(normalizedValue);
      }
      return '-';
    default:
      const defaultValue = parseFloat(normalizedValue);
      if (!isNaN(defaultValue) && isFinite(defaultValue)) {
        return formatMetricValue(normalizedValue, dataType, currency);
      }
      return '-';
  }
};

const SummaryMonthlyMetricsTable = ({
  brandId,
  selectedMonth,
  loading,
  brandCurrency = 'TRY'
}) => {
  const [summaryMetrics, setSummaryMetrics] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currency, setCurrency] = useState(brandCurrency);

  useEffect(() => {
    setCurrency(brandCurrency);
  }, [brandCurrency]);

  useEffect(() => {
    const fetchSummaryMonthlyMetrics = async () => {
      if (!brandId || !selectedMonth) return;
      
      setIsLoading(true);
      try {
        // Özet aylık metrikleri getir - API servisinde summaryDisplayOrder'a göre sıralanmış olarak gelecek
        const summaryData = await monthlyMetricService.getMonthlyMetricSummary(brandId);
        
        // Sadece görünür metrikleri filtrele
        const visibleMetrics = summaryData.filter(metric => metric.isVisible);
        
        console.log('Alınan özet metrikler:', summaryData);
        console.log('Görünür metrikler:', visibleMetrics);
        
        setSummaryMetrics(visibleMetrics);

        // Tablo verilerini hazırla
        const monthsData = [];
        const currentMonth = dayjs(selectedMonth).startOf('month');
        
        // Tüm metriklerden mevcut tüm ay değerlerini topla (tarih-bazlı unique set)
        const allMonths = new Set();
        visibleMetrics.forEach(metric => {
          metric.values.forEach(v => {
            const monthYearStr = v.date.substring(0, 7); // YYYY-MM formatı
            allMonths.add(monthYearStr);
          });
        });
        
        // Tarihleri sırala (en yeniden en eskiye)
        const sortedMonths = Array.from(allMonths).sort().reverse();
        
        // İçinde bulunduğumuz ayı filtrelemek için mevcut ayın başlangıcını al
        const thisMonth = dayjs().startOf('month').format('YYYY-MM');
        
        // Her ay için satır oluştur (bu ayı hariç tut)
        sortedMonths.forEach(monthStr => {
          // İçinde bulunduğumuz aydan önceki ayları göster
          if (monthStr < thisMonth) {
            const monthDate = dayjs(monthStr);
            const monthRow = {
              month: monthDate.format('MMMM YYYY'),
              date: monthStr,
            };

            // Her metrik için değerleri ekle
            let hasDataForMonth = false;
            visibleMetrics.forEach(metric => {
              // API'den gelen tarih formatına uygun şekilde arama yap
              const monthValue = metric.values.find(v => 
                v.date === monthStr || v.date.startsWith(monthStr)
              );
              
              if (monthValue && monthValue.value !== null && monthValue.value !== undefined) {
                let cleanValue = monthValue.value;
                
                // Promise kontrolü
                if (typeof cleanValue === 'object' && cleanValue !== null && typeof cleanValue.then === 'function') {
                  console.warn('Promise değeri tespit edildi ve null olarak atandı:', cleanValue);
                  cleanValue = null;
                }
                
                // String olarak gelen geçersiz değerleri kontrol et
                if (typeof cleanValue === 'string') {
                  const lowerValue = cleanValue.toLowerCase();
                  if (lowerValue === 'nan' || lowerValue === 'infinity' || lowerValue === '-infinity' || 
                      lowerValue.includes('[object promise]')) {
                    cleanValue = null;
                  }
                }
                
                // NaN kontrolü
                if (typeof cleanValue === 'number' && (isNaN(cleanValue) || !isFinite(cleanValue))) {
                  cleanValue = null;
                }
                
                monthRow[`metric_${metric.id}`] = cleanValue;
                
                // Sadece geçerli veriler varsa hasDataForMonth = true yap
                if (cleanValue !== null) {
                  hasDataForMonth = true;
                }
              } else {
                monthRow[`metric_${metric.id}`] = null;
              }
            });

            // Sadece verisi olan ayları ekle
            if (hasDataForMonth) {
              monthsData.push(monthRow);
            }
          }
        });

        console.log('Oluşturulan tablo verisi:', monthsData);
        setTableData(monthsData);
      } catch (error) {
        console.error('Özet aylık metrikler yüklenirken hata:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSummaryMonthlyMetrics();
  }, [brandId, selectedMonth]);

  if (isLoading || loading) {
    return (
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <Spin />
        <p>Özet metrikler yükleniyor...</p>
      </div>
    );
  }

  if (!summaryMetrics.length) {
    return <Text type="secondary">Bu marka için görünür özet aylık metrik bulunamadı.</Text>;
  }

  const columns = [
    { data: 'month', title: 'Ay', readOnly: true, className: 'htLeft' },
    ...summaryMetrics.map(metric => ({
      data: `metric_${metric.id}`,
      title: metric.description || metric.name,
      readOnly: true,
      className: 'htCenter',
      renderer: function(instance, td, row, col, prop, value, cellProperties) {
        // Geçersiz değerleri kontrol et (genişletilmiş)
        if (value === null || value === undefined || value === Infinity || value === -Infinity || 
            value === 'Infinity' || value === '-Infinity' || value === '' || value === ' ' ||
            value === 'NaN' || (typeof value === 'string' && value.toLowerCase().includes('[object promise]')) ||
            (typeof value === 'number' && isNaN(value)) ||
            (typeof value === 'object' && value !== null && typeof value.then === 'function')) {
          td.innerHTML = '-';
          td.style.color = '#999';
        } else {
          // metricId'yi formatValue fonksiyonuna geçir
          const formattedValue = formatValue(value, metric.dataType, metric.id, currency);
          td.innerHTML = formattedValue;
          
          // Eğer formatValue "-" döndürdüyse renklendirme yapma
          if (formattedValue === '-') {
            td.style.color = '#999';
          } else {
            // 10094 metriği için özel renklendirme veya tüm metrikler için negatif-pozitif renklendirme
            const numValue = parseFloat(value);
            if (!isNaN(numValue) && isFinite(numValue)) {
              if (metric.id === 10094 || metric.id === 10102) {
                // 10094 metriği için özel renklendirme (negatif: kırmızı, pozitif: yeşil)
                // Günlük metriklerdeki gibi daha açık renkler kullan
                td.style.backgroundColor = numValue < 0 ? '#ff17001f' : '#52c41a29';
                td.style.color = '#000'; // Metin rengi siyah
                td.style.fontWeight = 'bold';
              } else {
                // Diğer metrikler için negatif renklendirme kaldırıldı
                td.style.color = '#000';
              }
            } else {
              td.style.color = '#000';
            }
          }
        }
        
        // Tüm hücrelerin içeriğini ortala
        td.style.textAlign = 'center';
        td.style.verticalAlign = 'middle';
        
        return td;
      }
    }))
  ];

  return (
    <div style={{ width: '100%', height: 'auto', overflow: 'visible', marginTop: '20px' }}>
      <HotTable
        data={tableData}
        columns={columns}
        colHeaders={true}
        rowHeaders={false}
        width="100%"
        height={13 * 25 + 45} // 13 satır (her biri 25px) + başlık yüksekliği (45px)
        autoRowSize={false} // Otomatik satır boyutlandırmayı devre dışı bırak
        autoColumnSize={true}
        stretchH="all"
        licenseKey="non-commercial-and-evaluation"
        readOnly={true}
        className="htCenter"
        // HTML başlıkları etkinleştir
        allowHtml={true}
        // Satır yüksekliğini ayarla
        rowHeights={25}
        // Başlık yüksekliği
        columnHeaderHeight={45}
        // Sol sütunu sabit yapma (ay sütunu)
        fixedColumnsLeft={1}
        afterGetColHeader={(col, TH) => {
          if (col >= 0 && summaryMetrics[col - 1]) { // col-1 çünkü ilk sütun ay sütunu
            const metric = summaryMetrics[col - 1];
            const spanElement = TH.querySelector('.colHeader');
            if (spanElement && metric.description) {
              const words = metric.description.split(' ');
              
              if (words.length > 2) {
                const firstPart = words.slice(0, 2).join(' ');
                const secondPart = words.slice(2).join(' ');
                
                spanElement.innerHTML = `
                  <div style="line-height: 1.2; white-space: normal; display: flex; flex-direction: column; justify-content: center; height: 100%;">
                    <div style="text-align: center;">${firstPart}</div>
                    <div style="text-align: center;">${secondPart}</div>
                  </div>
                `;
              }
              
              TH.style.height = 'auto';
              TH.style.minHeight = '45px';
              TH.style.whiteSpace = 'normal';
              TH.style.verticalAlign = 'middle';
            }
          }
        }}
      />
    </div>
  );
};

export default SummaryMonthlyMetricsTable; 